import React from 'react';
import { Button, Dropdown, Space, message } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import {
  downloadSalesAnalyticsExport,
  type SalesAnalyticsFilterParams,
  type SalesAnalyticsTab,
} from '@/services/salesAnalytics.service';
import { openEngineReportPrint } from '@/components/print/openEngineReportPrint';

export interface SalesAnalyticsToolbarProps {
  tab: SalesAnalyticsTab;
  tabTitle: string;
  filters: SalesAnalyticsFilterParams;
  filterSummary: string;
  printColumns: { key: string; label: string; align?: 'left' | 'right' }[];
  printRows: Record<string, string | number>[];
  printKpiLines?: Array<{ label: string; value: string }>;
}

export const SalesAnalyticsToolbar: React.FC<SalesAnalyticsToolbarProps> = ({
  tab,
  tabTitle,
  filters,
  filterSummary,
  printColumns,
  printRows,
  printKpiLines,
}) => {
  const handlePrint = () => {
    if (!printRows.length && !printKpiLines?.length) {
      message.warning('Nothing to print for this tab yet.');
      return;
    }
    openEngineReportPrint({
      title: `Sales Analytics — ${tabTitle}`,
      documentNumber: `${filters.date_from ?? ''} to ${filters.date_to ?? ''}`,
      metaLines: [
        { label: 'Filters', value: filterSummary },
        ...(printKpiLines ?? []).map((k) => ({ label: k.label, value: k.value })),
      ],
      columns: printColumns.map((c) => ({
        key: c.key,
        label: c.label,
        align: c.align,
        widthPct: c.align === 'right' ? 18 : undefined,
      })),
      lines: printRows.map((r, i) => ({
        lineNumber: i + 1,
        description: String(printColumns[0] ? r[printColumns[0].key] ?? '' : ''),
        cells: Object.fromEntries(printColumns.map((c) => [c.key, String(r[c.key] ?? '')])),
      })),
      docType: 'sales-analytics-report',
    });
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      await downloadSalesAnalyticsExport(tab, filters, format);
      message.success(format === 'xlsx' ? 'Excel export downloaded' : 'CSV export downloaded');
    } catch (e) {
      message.error((e as Error)?.message ?? 'Export failed');
    }
  };

  const exportItems: MenuProps['items'] = [
    { key: 'xlsx', label: 'Excel (XLSX)', onClick: () => void handleExport('xlsx') },
    { key: 'csv', label: 'CSV', onClick: () => void handleExport('csv') },
  ];

  return (
    <Space wrap>
      <Button icon={<PrinterOutlined />} onClick={handlePrint}>
        Print / PDF
      </Button>
      <Dropdown menu={{ items: exportItems }} trigger={['click']}>
        <Button icon={<DownloadOutlined />}>Export</Button>
      </Dropdown>
    </Space>
  );
};

function downloadClientCsv(filename: string, header: string[], rows: (string | number)[][]) {
  const lines = [
    header.join(','),
    ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
  ].join('\r\n');
  const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export { downloadClientCsv };
